import {Injectable} from '@angular/core';
import {TorchTokenService} from './torch-token.service';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, of, timeout} from 'rxjs';
import {TorchUserService} from './torch-user.service';
import {catchError, filter, switchMap, map} from 'rxjs/operators';
import {Burly} from 'kb-burly';
import {UserSetting} from '../models/user-setting.model';

@Injectable({
  providedIn: 'root'
})
export class TorchUserSettingsService {

  public settings: BehaviorSubject<UserSetting[]> = new BehaviorSubject<UserSetting[]>([]);


  constructor(protected config: TorchAPIConfiguration,
              protected httpClient: HttpClient,
              private userService: TorchUserService,
              private tokenService: TorchTokenService) {

    this.userService.currentTorchUser.subscribe(user => this.settings.next(user.settings));
  }

  getSetting(name: string): Observable<UserSetting> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/user-settings')
      .addSegment(`/${name}`)
      .get;

    return this.getGraphToken().pipe(
      switchMap((token: string) => {
        if (!!token) {
          return this.httpClient.get<UserSetting>(endpointURL, {
            responseType: 'json',
            headers: {
              msalToken: token
            }
          });
        }
      }),
    );
  }

  /**
   * Get a UserSetting with default value
   * @param name - Name of the UserSetting (string)
   * @param defaultValue - Default value of the setting (string|number|boolean)
   * @param forceFetch - Force fetch the latest setting from the backend (default: false)
   */
  getSettingDefault(name: string,
                    defaultValue: string | number | boolean,
                    forceFetch: boolean = false): Observable<UserSetting> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/user-settings')
      .addSegment(`/${name}`)
      .get;

    const defaultValueSetting = UserSetting.fixedSetting(name, defaultValue);

    const currentSettings = this.settings.value;

    if (!!currentSettings && currentSettings.length > 0) {
      const existingSetting = currentSettings.find(setting => setting.name === name);

      if (!!existingSetting) {
        return of(existingSetting);
      }
    }

    return this.getGraphToken().pipe(
      filter(token => !!token),
      switchMap((token: string) => this.httpClient.get<UserSetting[]>(endpointURL, {
          responseType: 'json',
          headers: {
            msalToken: token
          }
        }).pipe(
          timeout({
            each: 2000,
            with: () => of([defaultValueSetting]),
          }),
        )
      ),
      map(settings => {
        if (settings.length > 0 && !!settings[0]) {
          return settings[0];
        }

        return defaultValueSetting;
      }),
      catchError(() => of(defaultValueSetting))
    );
  }

  updateSetting(name: string, value: any) {
    const body = {name, value};
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/user-settings')
      .get;

    this.getGraphToken().pipe(
      switchMap((token: string) => {
        if (!!token) {
          return this.httpClient.patch<UserSetting>(endpointURL, body, {
            responseType: 'json',
            headers: {
              msalToken: token
            }
          });
        }
      }),
    ).subscribe(userSetting => {
      const userSettings = (this.settings.value || []).filter(setting => setting.name !== userSetting.name);

      userSettings.push(userSetting);

      this.settings.next(userSettings);
    });
  }

  private getGraphToken() {
    return this.tokenService.getGraphToken().pipe(
      filter(token => !!token)
    );
  }
}
